export const MAX_PHOTO_INPUT_MB = 15;
export const MAX_PHOTO_INPUT_BYTES = MAX_PHOTO_INPUT_MB * 1024 * 1024;
const MAX_ORIGINAL_EDGE = 2400;
const MAX_THUMBNAIL_EDGE = 640;
const OUTPUT_MIME_TYPE = 'image/jpeg';
const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface ProcessedPhoto {
  originalBytes: Uint8Array;
  thumbnailBytes: Uint8Array;
  mimeType: typeof OUTPUT_MIME_TYPE;
  width: number;
  height: number;
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

function dimensions(width: number, height: number, maximumEdge: number): { width: number; height: number } {
  const scale = Math.min(1, maximumEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if ('createImageBitmap' in globalThis) {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
  }

  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function canvasBlob(source: CanvasImageSource, sourceWidth: number, sourceHeight: number, maximumEdge: number, quality: number): Promise<Blob> {
  const target = dimensions(sourceWidth, sourceHeight, maximumEdge);
  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('This browser could not prepare the photo.');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, target.width, target.height);
  context.drawImage(source, 0, 0, target.width, target.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('This browser could not encode the photo.')), OUTPUT_MIME_TYPE, quality);
  });
}

export function validatePhotoFile(file: File): void {
  if (!SUPPORTED_TYPES.has(file.type)) throw new Error('Choose a JPEG, PNG or WebP image.');
  if (file.size <= 0) throw new Error('The selected image is empty.');
  if (file.size > MAX_PHOTO_INPUT_BYTES) throw new Error(`Choose an image smaller than ${MAX_PHOTO_INPUT_MB} MB.`);
}


export async function createPhotoThumbnail(file: File): Promise<Uint8Array> {
  validatePhotoFile(file);
  let decoded: DecodedImage | null = null;
  try {
    decoded = await decodeImage(file);
    if (!decoded.width || !decoded.height) throw new Error('The selected image has no readable dimensions.');
    const thumbnailBlob = await canvasBlob(
      decoded.source,
      decoded.width,
      decoded.height,
      MAX_THUMBNAIL_EDGE,
      0.8,
    );
    return new Uint8Array(await thumbnailBlob.arrayBuffer());
  } catch (cause) {
    if (cause instanceof Error) throw cause;
    throw new Error('The selected image could not be read.');
  } finally {
    decoded?.release();
  }
}

export async function processPhotoFile(file: File): Promise<ProcessedPhoto> {
  validatePhotoFile(file);
  let decoded: DecodedImage | null = null;
  try {
    decoded = await decodeImage(file);
    if (!decoded.width || !decoded.height) throw new Error('The selected image has no readable dimensions.');
    const [originalBlob, thumbnailBlob] = await Promise.all([
      canvasBlob(decoded.source, decoded.width, decoded.height, MAX_ORIGINAL_EDGE, 0.88),
      canvasBlob(decoded.source, decoded.width, decoded.height, MAX_THUMBNAIL_EDGE, 0.8),
    ]);
    if (originalBlob.size > MAX_PHOTO_INPUT_BYTES) throw new Error(`The processed image is still larger than ${MAX_PHOTO_INPUT_MB} MB.`);
    const target = dimensions(decoded.width, decoded.height, MAX_ORIGINAL_EDGE);
    return {
      originalBytes: new Uint8Array(await originalBlob.arrayBuffer()),
      thumbnailBytes: new Uint8Array(await thumbnailBlob.arrayBuffer()),
      mimeType: OUTPUT_MIME_TYPE,
      width: target.width,
      height: target.height,
    };
  } catch (cause) {
    if (cause instanceof Error) throw cause;
    throw new Error('The selected image could not be read.');
  } finally {
    decoded?.release();
  }
}
